<template>
  <div class="relative">
    <el-button
      class="ai-button [&>span]:gap-2"
      type="primary"
      size="default"
      data-testid="ai-chat-open-button"
      @click="toggleOpen(!isOpen)"
    >
      <magic-icon />
      <span>
        {{ $t('aichat.main.button.open') }}
      </span>
    </el-button>

    <th-drawer
      v-model="isOpen"
      :title="$t('aichat.main.title')"
      destroy-on-close
      class="th-drawer"
      append-to-body
      size="432px"
      @update:modelValue="toggleOpen"
    >
      <template #default>
        <div class="flex flex-col h-full overflow-hidden">
          <div class="flex flex-row items-center">
            <span class="text-th-primary title-icon">
              <magic-icon />
            </span>
            <span class="text-md text-th-secondary flex-1 font-bold">
              {{ $t('aichat.main.title') }}
            </span>
            <template v-if="isAgentChatEnabled">
              <ai-chat-new-chat
                class="new-chat-button"
                :disabled="chat.isStreaming"
                @click="chat.reset()"
              />
              <ai-chat-history />
            </template>
            <el-button
              text
              size="default"
              class="th:hover:text-th-primary el-button--text-icon title-button"
              :aria-label="$t('aichat.main.button.close')"
              data-testid="ai-chat-close-button"
              @click="toggleOpen(false)"
            >
              <close-icon />
            </el-button>
          </div>
          <hr class="th-divider divider" />
          <ai-chat />
          <div class="text-th-secondary-gray text-center warning">
            {{ $t('aichat.main.warning') }}
          </div>
        </div>
      </template>
    </th-drawer>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { MagicIcon, CloseIcon } from '@shared/assets/icons'

import AiChat from '@shared/views/ai-chat/index.vue'
import AiChatHistory from '@shared/views/ai-chat/components/controls/history.vue'
import AiChatNewChat from '@shared/views/ai-chat/components/controls/new-chat.vue'
import { useAgentChatStore } from '@shared/store/agent-chat'
import { useAuthStore } from '@shared/store/auth'
import { useFeatureFlag } from '@shared/composables/use-feature-flag'

const chat = useAgentChatStore()
const auth = useAuthStore()

const isOpen = ref(false)
const isAgentChatEnabled = useFeatureFlag('agent_chat')

const toggleOpen = (nextOpen: boolean): void => {
  if (isOpen.value === nextOpen) return
  isOpen.value = nextOpen
}

/* an account switch re-scopes the Dashboard without a reload; the previous tenant's chat must not stay visible */
watch(
  () => auth.user,
  () => {
    if (chat.syncTenant() && isOpen.value && isAgentChatEnabled.value) {
      chat.loadCatalogue()
    }
  }
)
</script>

<style scoped>
.title-icon {
  margin-right: 0.5rem;
}

.title-icon > svg {
  width: 1.25rem;
  height: 1.25rem;
}

.title-button + .title-button,
.new-chat-button + .title-button {
  margin-left: 0;
}

.divider {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.warning {
  font-size: 0.65rem;
  margin-top: 1rem;
}
</style>
